/**
 * ChatList / Home left pane (06 S6) — ListHeader (title + actions overflow),
 * SearchBar, then pinned + time-sorted ChatListItems. FAB on mobile.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquarePlus, MoreVertical, Users, Settings, LogOut } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { Avatar } from '../primitives/Avatar';
import { ChatListItem } from './ChatListItem';
import { SearchBar } from './SearchBar';
import styles from './ChatList.module.css';

export function ChatList() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const me = useChatStore((s) => s.me);
  const conversations = useChatStore((s) => s.conversations);
  const userById = useChatStore((s) => s.userById);
  const setLeftOverlay = useUiStore((s) => s.setLeftOverlay);

  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = conversations
      .filter((c) => !c.archived)
      .filter((c) => {
        if (!q) return true;
        const peer = c.peerId ? userById(c.peerId) : undefined;
        const name = c.type === 'group' ? c.title ?? '' : peer?.displayName ?? '';
        const last = c.lastMessage?.text ?? '';
        return name.toLowerCase().includes(q) || last.toLowerCase().includes(q);
      });
    return [...filtered].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.updatedAtMs - a.updatedAtMs;
    });
  }, [conversations, query, userById]);

  const pinned = visible.filter((c) => c.pinned);
  const rest = visible.filter((c) => !c.pinned);

  return (
    <div className={styles.pane}>
      <header className={styles.header}>
        <Avatar id={me.id} name={me.displayName} url={me.avatarUrl} size={40} />
        <span className={styles.brand}>Sinyal</span>
        <div className={styles.actions}>
          <button
            className={styles.iconBtn}
            onClick={() => setLeftOverlay({ kind: 'new-chat' })}
            aria-label="New chat"
          >
            <MessageSquarePlus size={22} />
          </button>
          <div className={styles.menuWrap}>
            <button
              className={styles.iconBtn}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-haspopup="menu"
            >
              <MoreVertical size={22} />
            </button>
            {menuOpen && (
              <>
                <div className={styles.menuScrim} onClick={() => setMenuOpen(false)} />
                <ul className={styles.menu} role="menu">
                  <li>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setLeftOverlay({ kind: 'new-group-members', selected: [] });
                        setMenuOpen(false);
                      }}
                    >
                      <Users size={18} /> New group
                    </button>
                  </li>
                  <li>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setLeftOverlay({ kind: 'settings', page: 'root' });
                        setMenuOpen(false);
                      }}
                    >
                      <Settings size={18} /> Settings
                    </button>
                  </li>
                  <li>
                    <button role="menuitem" onClick={() => setMenuOpen(false)}>
                      <LogOut size={18} /> Log out
                    </button>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </header>

      <SearchBar value={query} onChange={setQuery} />

      <div className={styles.scroll}>
        {visible.length === 0 && (
          <p className={styles.empty}>No chats yet — start a conversation.</p>
        )}
        {pinned.length > 0 && (
          <>
            <h2 className={styles.section}>Pinned</h2>
            <ul>
              {pinned.map((c) => (
                <ChatListItem
                  key={c.id}
                  conv={c}
                  active={c.id === chatId}
                  onOpen={() => navigate(`/chats/${c.id}`)}
                />
              ))}
            </ul>
          </>
        )}
        <ul>
          {rest.map((c) => (
            <ChatListItem
              key={c.id}
              conv={c}
              active={c.id === chatId}
              onOpen={() => navigate(`/chats/${c.id}`)}
            />
          ))}
        </ul>
      </div>

      <button
        className={styles.fab}
        onClick={() => setLeftOverlay({ kind: 'new-chat' })}
        aria-label="New chat"
      >
        <MessageSquarePlus size={24} />
      </button>
    </div>
  );
}
